import React, { useState, useEffect, useMemo } from 'react';
import { Wrench, Plus, Copy, Trash2, Filter, Layers, Tags, Coins, X } from 'lucide-react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import api from '../../services/api';
import styles from './SettingsPage.module.css';

const getInitialProjectId = () => {
  if (typeof window === 'undefined') return '';
  const stored = localStorage.getItem('selected_project_id');
  if (!stored) return '';
  const parsed = Number(stored);
  return Number.isNaN(parsed) ? '' : parsed;
};

const RepairCodeList = ({ launch }) => {
  const [codes, setCodes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dictionaries, setDictionaries] = useState([]);
  const [selectedProject, setSelectedProject] = useState(getInitialProjectId);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedDictionary, setSelectedDictionary] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [copyingForModel, setCopyingForModel] = useState(false);
  const [modelInput, setModelInput] = useState('');
  const [projectCatalog, setProjectCatalog] = useState({ models: [], codes: [] });
  const [catalogRefreshKey, setCatalogRefreshKey] = useState(0);
  const [formData, setFormData] = useState({
    code: '',
    model_name: 'Все',
    name: '',
    description: '',
    price: '',
    project: '',
    customers: [],
    dictionary: null,
  });

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
    fetchDictionaries();
  }, []);

  useEffect(() => {
    if (!launch) return;
    setSelectedProject(launch.projectId);
    setSelectedDictionary(launch.dictionary.value);
    setFormData({
      code: '', model_name: 'Все', name: '', description: '', price: '',
      project: launch.projectId,
      customers: launch.customers || [],
      dictionary: launch.dictionary,
    });
    setEditingId(null);
    setCopyingForModel(false);
    setModelInput('');
    setIsModalOpen(true);
  }, [launch]);

  useEffect(() => {
    if (!selectedProject) {
      setCodes([]);
      return;
    }
    fetchCodes();
  }, [selectedProject, selectedCustomer, selectedDictionary]);

  const catalogProjectId = formData.project || selectedProject;
  useEffect(() => {
    if (!catalogProjectId) {
      setProjectCatalog({ models: [], codes: [] });
      return undefined;
    }
    let active = true;
    api.get('/repairs/repair-codes/catalog/', { params: { project_id: catalogProjectId } })
      .then(({ data }) => {
        if (active) setProjectCatalog({ models: data.models || [], codes: data.codes || [] });
      })
      .catch(() => {
        if (active) setProjectCatalog({ models: [], codes: [] });
      });
    return () => { active = false; };
  }, [catalogProjectId, catalogRefreshKey]);

  const projectOptions = useMemo(
    () => projects.map(project => ({ value: project.id, label: project.name })),
    [projects]
  );

  const customerOptions = useMemo(
    () => customers.map(customer => ({ value: customer.id, label: customer.name })),
    [customers]
  );

  const repairDictionaryOptions = useMemo(() => {
    const applicable = dictionaries.filter(
      dictionary =>
        dictionary.dictionary_type === 'repair' &&
        (!selectedProject || dictionary.projects.some(project => project.id === selectedProject))
    );
    return applicable.map(dictionary => ({ value: dictionary.id, label: dictionary.name }));
  }, [dictionaries, selectedProject]);

  const knownModels = useMemo(() => Array.from(new Set(
    ['Все', ...projectCatalog.models].filter(Boolean)
  )).sort((a, b) => a === 'Все' ? -1 : b === 'Все' ? 1 : a.localeCompare(b, 'ru')), [projectCatalog.models]);
  const modelOptions = useMemo(() => knownModels.map(name => ({ value: name, label: name })), [knownModels]);
  const codeSuggestions = useMemo(() => {
    const query = formData.code.trim().toLocaleLowerCase('ru');
    return projectCatalog.codes.filter(code => !query || code.toLocaleLowerCase('ru').includes(query)).slice(0, 6);
  }, [formData.code, projectCatalog.codes]);
  const codeAlreadyUsed = projectCatalog.codes.some(code => code.toLocaleLowerCase('ru') === formData.code.trim().toLocaleLowerCase('ru'));

  const fetchProjects = async () => {
    try {
      const { data } = await api.get('/settings/projects/');
      setProjects(data);
    } catch (error) {
      toast.error('Не удалось загрузить проекты');
    }
  };

  const fetchCustomers = async () => {
    try {
      const { data } = await api.get('/settings/customers/');
      setCustomers(data);
    } catch (error) {
      toast.error('Не удалось загрузить заказчиков');
    }
  };

  const fetchDictionaries = async () => {
    try {
      const { data } = await api.get('/settings/dictionary-uploads/');
      setDictionaries(data);
    } catch (error) {
      toast.error('Не удалось загрузить справочники');
    }
  };

  const fetchCodes = async () => {
    if (!selectedProject) return;
    try {
      setLoading(true);
      const params = { project_id: selectedProject };
      if (selectedCustomer) params.customer_id = selectedCustomer;
      if (selectedDictionary) params.dictionary_id = selectedDictionary;
      const { data } = await api.get('/repairs/repair-codes/', { params });
      setCodes(data);
    } catch (error) {
      toast.error('Ошибка загрузки кодов ремонта');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    if (!selectedProject) {
      toast.warn('Сначала выберите проект');
      return;
    }
    setFormData({
      code: '',
      model_name: 'Все',
      name: '',
      description: '',
      price: '',
      project: selectedProject,
      customers: selectedCustomer ? customerOptions.filter(option => option.value === selectedCustomer) : [],
      dictionary: repairDictionaryOptions.find(option => option.value === selectedDictionary) || null,
    });
    setEditingId(null);
    setCopyingForModel(false);
    setModelInput('');
    setIsModalOpen(true);
  };

  const handleCopyForModel = (item) => {
    setFormData({
      code: item.code,
      model_name: '',
      name: item.name,
      description: item.description || '',
      price: '',
      project: item.project?.id || selectedProject,
      customers: customerOptions.filter(option =>
        item.customers?.some(customer => customer.id === option.value)
      ),
      dictionary: item.dictionary_upload
        ? repairDictionaryOptions.find(option => option.value === item.dictionary_upload) || null
        : null,
    });
    setEditingId(null);
    setCopyingForModel(true);
    setModelInput('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setCopyingForModel(false);
    setModelInput('');
    setFormData({
      code: '',
      model_name: 'Все',
      name: '',
      description: '',
      price: '',
      project: '',
      customers: [],
      dictionary: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const modelName = modelInput.trim() || formData.model_name.trim();
    if (!formData.project) {
      toast.error('Не выбран проект');
      return;
    }
    if (!formData.code.trim() || !formData.name.trim()) {
      toast.error('Заполните обязательные поля');
      return;
    }
    if (copyingForModel && (!modelName || modelName === 'Все' || formData.price === '')) {
      toast.error('Укажите новую модель и её стоимость');
      return;
    }

    const payload = {
      code: formData.code.trim(),
      model_name: modelName || 'Все',
      name: formData.name.trim(),
      description: formData.description,
      price: formData.price || '0',
      project_id: formData.project,
      customer_ids: formData.customers.map(option => option.value),
      dictionary_upload_id: formData.dictionary ? formData.dictionary.value : null,
    };

    try {
      if (editingId) {
        await api.put(`/repairs/repair-codes/${editingId}/`, payload);
        toast.success('Код ремонта обновлён');
      } else {
        await api.post('/repairs/repair-codes/', payload);
        toast.success('Код ремонта создан');
      }
      closeModal();
      fetchCodes();
      setCatalogRefreshKey(key => key + 1);
    } catch (error) {
      const errors = error.response?.data;
      const message = errors?.model_name?.[0] || errors?.dictionary_upload_id?.[0] || errors?.non_field_errors?.[0];
      toast.error(message || 'Ошибка сохранения кода ремонта');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      code: item.code,
      model_name: item.model_name || 'Все',
      name: item.name,
      description: item.description || '',
      price: item.price,
      project: item.project?.id || selectedProject || '',
      customers: customerOptions.filter(option =>
        item.customers.some(customer => customer.id === option.value)
      ),
      dictionary: item.dictionary_upload
        ? repairDictionaryOptions.find(option => option.value === item.dictionary_upload) ||
          { value: item.dictionary_upload, label: `Справочник #${item.dictionary_upload}` }
        : null,
    });
    setEditingId(item.id);
    setCopyingForModel(false);
    setModelInput('');
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить код ремонта?')) return;
    try {
      await api.delete(`/repairs/repair-codes/${id}/`);
      toast.success('Код ремонта удалён');
      fetchCodes();
    } catch (error) {
      toast.error('Ошибка удаления кода ремонта');
    }
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined) return '—';
    const number = Number(value);
    if (Number.isNaN(number)) return value;
    return number.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
  };

  const getDictionaryName = (dictionaryId) => {
    if (!dictionaryId) return 'Не указан';
    const found = dictionaries.find(dictionary => dictionary.id === dictionaryId);
    return found ? found.name : `Справочник #${dictionaryId}`;
  };

  const renderCustomers = (item) => {
    if (!item.customers?.length) {
      return <span className={styles.mutedText}>—</span>;
    }
    return item.customers.map(customer => (
      <span key={customer.id} className={styles.pill}>
        {customer.name}
      </span>
    ));
  };

  const selectedProjectOption = projectOptions.find(option => option.value === selectedProject) || null;
  const selectedCustomerOption = customerOptions.find(option => option.value === selectedCustomer) || null;
  const dictionaryFilterOption = repairDictionaryOptions.find(option => option.value === selectedDictionary) || null;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <Wrench size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Коды ремонта
        </h3>
        <button className={styles.addButton} onClick={openAddModal} disabled={!selectedProject}>
          <Plus size={18} />
          Добавить код
        </button>
      </div>

      <p className={styles.mutedText}>Для новой модели нажмите значок копирования у похожей работы — код и название сохранятся, останется указать модель и цену.</p>

      <div className={styles.filtersRow}>
        <div className={styles.filterBlock}>
          <label>
            <Filter size={14} />
            Проект
          </label>
          <Select
            className={styles.select}
            classNamePrefix="react-select"
            placeholder="Выберите проект"
            value={selectedProjectOption}
            onChange={(option) => setSelectedProject(option ? option.value : '')}
            options={projectOptions}
            isClearable
          />
        </div>
        <div className={styles.filterBlock}>
          <label>
            <Tags size={14} />
            Справочник
          </label>
          <Select
            className={styles.select}
            classNamePrefix="react-select"
            placeholder="Все справочники"
            value={dictionaryFilterOption}
            onChange={(option) => setSelectedDictionary(option ? option.value : '')}
            options={repairDictionaryOptions}
            isClearable
          />
        </div>
        <div className={styles.filterBlock}>
          <label>
            <Layers size={14} />
            Заказчик
          </label>
          <Select
            className={styles.select}
            classNamePrefix="react-select"
            placeholder="Все заказчики"
            value={selectedCustomerOption}
            onChange={(option) => setSelectedCustomer(option ? option.value : '')}
            options={customerOptions}
            isClearable
          />
        </div>
      </div>

      {!selectedProject && (
        <div className={styles.infoBanner}>
          Выберите проект, чтобы управлять кодами ремонта.
        </div>
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Загрузка кодов ремонта...</p>
          </div>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          {codes.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <Wrench size={48} />
              </div>
              <h3>Нет кодов ремонта</h3>
              <p>Добавьте первый код ремонта, чтобы рассчитывать сметы автоматически</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Код</th>
                  <th>Описание</th>
                  <th>Модель</th>
                  <th>Стоимость</th>
                  <th>Справочник</th>
                  <th>Заказчики</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(item => (
                  <tr key={item.id} className={styles.clickableRow} onClick={() => handleEdit(item)}>
                    <td>
                      <div className={styles.inlineCode}>
                        <Layers size={14} />
                        {item.code}
                      </div>
                    </td>
                    <td>
                      <strong>{item.name}</strong>
                      <div className={styles.mutedText}>{item.description || '—'}</div>
                    </td>
                    <td>{item.model_name || 'Все'}</td>
                    <td>
                      <div className={styles.priceCell}>
                        <Coins size={14} />
                        {formatPrice(item.price)}
                      </div>
                    </td>
                    <td>{getDictionaryName(item.dictionary_upload)}</td>
                    <td className={styles.pillColumn}>{renderCustomers(item)}</td>
                    <td>
                      <button className={styles.actionButton} onClick={(event) => { event.stopPropagation(); handleCopyForModel(item); }} title="Добавить эту работу для другой модели" aria-label={`Добавить работу ${item.code} для другой модели`}>
                        <Copy size={16} />
                      </button>
                      <button className={styles.actionButton} onClick={(event) => { event.stopPropagation(); handleDelete(item.id); }} title="Удалить">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <Wrench size={20} />
                {editingId ? 'Редактировать код ремонта' : copyingForModel ? 'Добавить цену для другой модели' : 'Добавить код ремонта'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {copyingForModel && (
                <div className={styles.infoBanner}>Работа скопирована. Укажите новую модель и стоимость; исходная запись не изменится.</div>
              )}
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label>Проект</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите проект"
                    value={projectOptions.find(option => option.value === formData.project) || null}
                    onChange={(option) => setFormData({ ...formData, project: option ? option.value : '' })}
                    options={projectOptions}
                    isClearable
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Код</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Введите код или выберите существующий"
                    list="repair-code-project-codes"
                    required
                  />
                  <datalist id="repair-code-project-codes">
                    {projectCatalog.codes.map(code => <option key={code} value={code} />)}
                  </datalist>
                  <small className={styles.formHelp}>В проекте уже используется {projectCatalog.codes.length} кодов. Можно выбрать существующий код для новой модели или ввести новый.</small>
                  {codeSuggestions.length > 0 && (
                    <div className={styles.codeSuggestions} aria-label="Коды проекта">
                      {codeSuggestions.map(code => (
                        <button key={code} type="button" className={styles.codeSuggestionButton} onClick={() => setFormData({ ...formData, code })}>{code}</button>
                      ))}
                    </div>
                  )}
                  {codeAlreadyUsed && <small className={styles.formHelp}>Этот код уже есть в проекте. Для другой модели его можно использовать повторно.</small>}
                </div>
                <div className={styles.formGroup}>
                  <label>Модель</label>
                  <CreatableSelect
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите или введите новую модель"
                    options={modelOptions}
                    value={modelOptions.find(option => option.value === formData.model_name) || (formData.model_name ? { value: formData.model_name, label: formData.model_name } : null)}
                    inputValue={modelInput}
                    onInputChange={(value, meta) => { if (meta.action === 'input-change') setModelInput(value); }}
                    onChange={(option) => { setFormData({ ...formData, model_name: option?.value || '' }); setModelInput(''); }}
                    onCreateOption={(value) => { setFormData({ ...formData, model_name: value.trim() }); setModelInput(''); }}
                    formatCreateLabel={(value) => `Добавить новую модель «${value}»`}
                    noOptionsMessage={() => 'Введите название новой модели'}
                    isClearable
                  />
                  <small className={styles.formHelp}>Модели подгружаются из оборудования проекта. Если нужной нет, напишите её название и нажмите Enter.</small>
                </div>
                <div className={styles.formGroup}>
                  <label>Название</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Например, Замена системной платы"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Цена</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    required={copyingForModel}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Заказчики</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите заказчиков"
                    value={formData.customers}
                    onChange={(value) => setFormData({ ...formData, customers: value || [] })}
                    options={customerOptions}
                    isMulti
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Справочник</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Привязка к справочнику (необязательно)"
                    value={formData.dictionary}
                    onChange={(value) => setFormData({ ...formData, dictionary: value })}
                    options={repairDictionaryOptions}
                    isClearable
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Описание</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Расширенное описание работ"
                  />
                </div>
                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Сохранить изменения' : copyingForModel ? 'Добавить работу для модели' : 'Создать код'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepairCodeList;
